import styles from './adjustmentNotePDFPage.module.css'
import { useEffect, useMemo, useState } from 'react'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { useBranchLoaders } from '../../../../manager'
import { PDFHeader } from '../../../../../features/ui'
import { AdjustmentNoteItemPDFRow } from './types'
import { TableName } from '../../../../../tables'
import { firstItem } from '../../../../../utils/listUtils'
import { addressHelpers } from '../../../../../features/address'
import { useSalesHooks, SaleOperation, Customer, useCustomerLoaders, saleOperationHelpers } from '../../../../sales'
import { FiscalDocumentItem, FiscalDocumentType } from '../../../../fiscal-documents'
import { useEmployeeLoaders } from '../../../loaders/employeeLoaders'
import { AdjustmentNote, AdjustmentNoteType, DocumentKey, Employee } from '../../../state/types'

type Props = {
    pageIndex: number
    adjustmentNote: AdjustmentNote
    operation: SaleOperation
    items: FiscalDocumentItem[]
    pagesAmount: number
}

export const AdjustmentNotePDFPage = (props: Props) => {
    const { pageIndex, adjustmentNote, operation, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1

    const [customer, setCustomer] = useState<Customer>()
    const [seller, setSeller] = useState<Employee>()
    const [rows, setRows] = useState<AdjustmentNoteItemPDFRow[]>([])

    const { describeAddress } = addressHelpers
    const { getTable } = saleOperationHelpers
    const { formatDate, formatDateTime, formatPrice, formatIdentifier, formatPhone } = useDataTypesHooks()
    const { getDocumentFooter } = useSalesHooks()

    const branchLoaders = useBranchLoaders()
    const customerLoaders = useCustomerLoaders()
    const employeeLoaders = useEmployeeLoaders()

    const branch = useMemo(() => branchLoaders().findBranch(operation.branchId)!, [operation])
    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.SALE), [getDocumentFooter])
    const operationTable = useMemo(() => getTable(operation), [operation])

    const adjustmentNoteTypeLabel = (type: AdjustmentNoteType) => {
        const labels = new Map<AdjustmentNoteType, string>()
            .set(AdjustmentNoteType.CREDIT, 'Crédito')
            .set(AdjustmentNoteType.DEBIT, 'Débito')
        return labels.get(type)!
    }

    // Manual credit notes (price adjustments) aren't tied to products: measure and quantity don't apply
    const isPriceAdjustment = !!adjustmentNote.reason

    const columns = [
        { name: 'product', label: 'Producto' },
        { name: 'measure', label: 'Medida' },
        { name: 'unitTotal', label: 'P. Unitario' },
        { name: 'quantity', label: 'Cantidad' },
        { name: 'total', label: 'P. Total' }
    ].filter(column => !isPriceAdjustment || !['measure', 'quantity'].includes(column.name))

    useEffect(() => {
        employeeLoaders().loadEmployee(operation.sellerId).then(setSeller)
        customerLoaders().loadCustomer(operation.customerId).then(setCustomer)
    }, [operation])

    useEffect(() => {
        // The adjustment may be split by VAT rate (for the fiscal document), but it's shown as a single line
        if (isPriceAdjustment) {
            setRows([{
                product: 'Ajuste de precio',
                measure: '',
                unitTotal: formatPrice(adjustmentNote.amount),
                quantity: '',
                total: formatPrice(adjustmentNote.amount)
            }])
            return
        }
        setRows(items.map(item => ({
            product: item.name,
            measure: item.unitTypeName,
            unitTotal: formatPrice(item.unitTotal),
            quantity: `${item.quantity} ${item.unitTypeName}`,
            total: formatPrice(item.unitTotal * item.quantity)
        })))
    }, [items])

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <PDFHeader noValidTicketLabel fiscalDocumentType={FiscalDocumentType.CREDIT_NOTE} />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <span className={styles.adjustmentNoteTitle}>
                            Nota de {adjustmentNoteTypeLabel(adjustmentNote.type)} #{adjustmentNote.code}
                        </span>
                        <div className={styles.date}>
                            Fecha: {formatDateTime(new Date(adjustmentNote.date))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Cliente: {customer?.name}</span>
                            <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                            <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                            <span>Teléfono: {customer && customer.phones.length > 0 ? formatPhone(firstItem(customer.phones)) : '-'}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>
                                {operationTable === TableName.SALE_ORDERS ? 'Venta' : 'Acopio'} #{operation.code}
                            </span>
                            <span>Fecha de Operación: {formatDate(new Date(operation.creationDate))}</span>
                        </div>
                    </div>
                </>)}

                {rows.length > 0 && (
                    <>
                        <div className={styles.tableRow}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        {columns.map((column, index) => (
                                            <th key={index}>
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={index}>
                                            {columns.map((column, columnIndex) => (
                                                <td key={columnIndex}>
                                                    {row[column.name as keyof AdjustmentNoteItemPDFRow]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={styles.pagination}>
                            {pageIndex + 1} / {pagesAmount}
                        </div>
                    </>
                )}

                {isLastPage && (
                    <div className={styles.totalLine}>
                        Monto Total: <strong>{formatPrice(adjustmentNote.amount)}</strong>
                    </div>
                )}
                <div className={styles.footer}>
                    <div className={styles.separator} />
                    <div className={`${styles.row} ${styles.footerRow}`}>
                        <span>
                            Sucursal {branch.name}: {describeAddress(branch.address)}{branch.email ? ` - Mail: ${branch.email}` : ''}
                        </span>
                        <span>
                            {documentFooter}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}
